import { useMemo, useState } from "react";
import { useBoxBuilder } from "@/lib/store";
import { countSelectedItems } from "@/lib/pricing";
import type { BoxItem } from "@/lib/types";
import { cn } from "@/lib/cn";
import ItemCard from "./ItemCard";

const ALL_CATEGORIES = "__all__";

export default function ItemGrid({
  items,
  limit,
}: {
  items: BoxItem[];
  limit: number;
}) {
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  const selectedItems = useBoxBuilder((s) => s.selectedItems);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const item of items) {
      if (item.category) set.add(item.category);
    }
    return ["All", ...Array.from(set).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      const ao = a.sortOrder ?? 0;
      const bo = b.sortOrder ?? 0;
      if (ao !== bo) return ao - bo;
      return a.title.localeCompare(b.title);
    });
    if (category === ALL_CATEGORIES) return sorted;
    return sorted.filter((i) => (i.category ?? "Uncategorized") === category);
  }, [items, category]);

  const totalSelected = countSelectedItems(selectedItems);
  const atLimit = limit > 0 && totalSelected >= limit;

  return (
    <>
      {categories.length > 2 ? (
        <div className="mb-4 -mx-1 flex overflow-x-auto px-1 pb-2">
          <div className="flex gap-2">
            {categories.map((cat) => {
              const value = cat === "All" ? ALL_CATEGORIES : cat;
              const active = category === value;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(value)}
                  className={cn(
                    "whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition",
                    active
                      ? "bg-ink-800 text-white"
                      : "bg-white text-ink-500 ring-1 ring-ink-100 hover:text-ink-800",
                  )}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <div className="card p-8 text-center text-ink-500">
          No items in this category right now.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((item) => (
            <ItemCard key={item.id} item={item} atLimit={atLimit} />
          ))}
        </div>
      )}
    </>
  );
}
